<script setup lang="ts">
import { ref, toRefs, watch } from 'vue';
import { EMIT } from '@/enum';

interface ReferralInterface {
  id: number;
  service_provider_id: number | null;
  index: number;
  student_name: string;
  page: number;
}

interface PropsInterface {
  referrals: ReferralInterface[];
  open: boolean;
}

const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE, 'update-referrals']);

const props = defineProps<PropsInterface>();

const { referrals, open } = toRefs(props);

const referralsArray = ref<ReferralInterface[]>([...referrals.value]);

watch(referrals, (newReferrals) => (referralsArray.value = [...newReferrals]));

const onCancel = () => emit(EMIT.CANCEL);

const onArchive = () => emit(EMIT.SAVE);
</script>

<template>
  <ConfirmAction
    v-if="open"
    header="Archive Referral Students"
    accept-label="Archive"
    @accept="onArchive"
    @cancel="onCancel"
  >
    <div class="ml-1">
      <div class="flex">
        <div class="flex">
          <Icon name="warning" class="flex-1 p-1 text-primary-800" />
        </div>
        <div class="flex-1 p-2">Are you sure you want to archive the following students?</div>
      </div>
      <div>
        <p class="mb-3 mt-3">This action will:</p>
        <ol>
          <li>Remove all future events for these students</li>
          <li>Remove all connections to their Service Providers</li>
          <li>Remove access to the system for these students (if applicable)</li>
          <li>Set the archive reason as Inactive</li>
        </ol>
      </div>
      <div v-if="referrals.length" class="section">
        <div class="name-grid">
          <div v-for="referral in referrals" :key="referral.id" class="name-item">
            <span>{{ referral.student_name }} (Page {{ referral.page }})</span>
          </div>
        </div>
      </div>
    </div>
  </ConfirmAction>
</template>

<style scoped lang="scss">
.confirm-action {
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;

  .p-dialog-header,
  .p-dialog-content {
    background: white !important;
  }

  .p-dialog-header {
    padding: 10px !important;

    span {
      font-size: 1.25rem !important;
    }
  }

  .p-dialog-content {
    padding: 10px !important;

    .text-red-600 {
      color: $red !important;
    }
  }

  .p-dialog-footer {
    padding: 15px !important;
  }

  .section {
    margin-top: 15px;

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      margin-bottom: 10px;
    }
  }

  .name-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 10px;
  }

  .name-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    line-height: 1.5;
  }

  .delete-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
  }
}
</style>
